import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertCircle, ArrowLeft, Download, FileText, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/features/LoadingSpinner'
import { PDFViewer } from '@/components/features/PDFViewer'
import { ImportedLanguageWarning } from '@/components/features/imported-language/ImportedLanguageWarning'
import { getQuoteVersionLanguageCheck } from '@/components/features/imported-language/imported-language'
import {
  generateQuoteVersionDocumentV2,
  getProjectQuoteVersionPdfUrlV2,
  getQuoteVersionPreviewPdfV2,
} from '@/components/features/project-detail/quotes/quotes-v2.api'

export default function QuoteV2PreviewPage() {
  const navigate = useNavigate()
  const { id: projectId, quoteId, versionId } = useParams<{ id: string; quoteId: string; versionId: string }>()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)

  const [isGenerating, setIsGenerating] = useState(false)
  const [validUntil, setValidUntil] = useState<string>('')

  const defaultValidUntil = useMemo(() => {
    const formatDateInputValue = (date: Date) => {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }

    const d = new Date()
    d.setDate(d.getDate() + 30)
    return formatDateInputValue(d)
  }, [])

  useEffect(() => {
    setValidUntil(defaultValidUntil)
  }, [defaultValidUntil])

  useEffect(() => {
    if (!projectId || !quoteId || !versionId) {
      navigate(`/projects/${projectId}?tab=quotes`)
      return
    }

    let isMounted = true
    let urlToRevoke: string | null = null

    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // No language override: the preview must show the very document the customer will
        // receive, and the backend resolves that from the project's "Langue du document"
        // (then the client's). Passing one wins over both - it is rule #1 of the chain.
        const blob = await getQuoteVersionPreviewPdfV2(projectId, quoteId, versionId)
        if (!isMounted) return

        setPdfBlob(blob)

        urlToRevoke = URL.createObjectURL(blob)
        setPdfObjectUrl(urlToRevoke)
      } catch {
        if (!isMounted) return
        setError("Impossible de charger l'aperçu PDF")
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    load()

    return () => {
      isMounted = false
      if (urlToRevoke) {
        URL.revokeObjectURL(urlToRevoke)
      }
    }
  }, [projectId, quoteId, versionId, navigate])

  const handleBack = () => {
    navigate(`/projets/${projectId}/devis-v2/${quoteId}/versions/${versionId}/edit`)
  }

  const handleDownloadPreview = () => {
    if (!pdfBlob) return

    const url = URL.createObjectURL(pdfBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Apercu-Devis.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleGenerate = async () => {
    if (!projectId || !quoteId || !versionId) return
    if (!validUntil) return

    try {
      setIsGenerating(true)
      await generateQuoteVersionDocumentV2(projectId, quoteId, versionId, { validUntil })

      try {
        const pdf = await getProjectQuoteVersionPdfUrlV2(projectId, quoteId, versionId)
        if (pdf.url) {
          window.open(pdf.url, '_blank', 'noopener,noreferrer')
        }
      } catch {
        // ignore
      }

      toast.success('Devis généré')
      navigate(`/projects/${projectId}?tab=quotes`)
    } catch {
      toast.error('Erreur lors de la génération du devis')
    } finally {
      setIsGenerating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !pdfObjectUrl) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-4">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">Erreur de chargement</h2>
            <p className="text-sm text-muted-foreground">{error || 'Aperçu non disponible'}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <Button onClick={() => window.location.reload()}>Réessayer</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-muted/30">
      <div className="sticky top-0 z-50 bg-background border-b shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button data-testid="preview-back-btn" variant="ghost" size="sm" onClick={handleBack} disabled={isGenerating}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au devis
            </Button>
            <div className="border-l pl-3">
              <h1 className="text-lg font-semibold">Aperçu du devis</h1>
              <p className="text-xs text-muted-foreground">Aperçu non contractuel</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPreview} disabled={isGenerating} className="gap-2">
              <Download className="h-4 w-4" />
              Télécharger
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="h-full grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 p-4">
          <div className="h-full overflow-hidden bg-gray-100 rounded-lg p-2">
            <div className="h-full w-full bg-white shadow-2xl rounded-lg overflow-hidden">
              <PDFViewer pdfSource={pdfObjectUrl} title="Quote V2 Preview" />
            </div>
          </div>

          <div className="bg-background border rounded-lg p-4 h-fit max-h-full overflow-y-auto">
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium">Actions</div>
                <div className="text-xs text-muted-foreground">Vérifie l’aperçu avant de générer le document.</div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Validité du devis</div>
                <input
                  data-testid="preview-valid-until"
                  type="date"
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  disabled={isGenerating}
                />
              </div>

              {projectId && quoteId && versionId ? (
                <ImportedLanguageWarning load={() => getQuoteVersionLanguageCheck(projectId, quoteId, versionId)} />
              ) : null}

              <Button data-testid="preview-generate-btn" onClick={handleGenerate} disabled={isGenerating || !validUntil} size="lg" className="w-full gap-2">
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Génération…
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Générer le devis
                  </>
                )}
              </Button>

              <Button onClick={handleBack} variant="outline" className="w-full" disabled={isGenerating}>
                Retour
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
